/**
 * @fileoverview When the Discover row's start→end range line earns its place
 * (WEB-788 / DPL-19 / PAR-28).
 *
 * WEB-293 introduced the explicit two-sided range
 * (`TUE, AUG 18 · 10:15 PM → WED, AUG 19 · 12:15 AM EDT`) to replace an opaque
 * `PM · → MAY 22` notation that never said whether an event started, ended, or
 * *spanned* those days. It is gated on `formatEventTimes(...).isMultiDay`,
 * which is true for **any** crossing of local midnight — and a 10:15 PM show
 * ending at 12:15 AM is the ordinary evening case, not a multi-day run. On
 * those rows the line restates a start time the spine has already printed and
 * a date the day-section heading has already printed, which is precisely the
 * "same fact, three times, two typefaces" that PAR-28 reports.
 *
 * So the line renders only when the event genuinely reaches a **later day**,
 * not merely past midnight of the same night.
 *
 * Pure and React-free (unlike `eventTiming.ts`, which owns the live-window
 * hook) so the card, tests, and any future server caller share one predicate.
 *
 * @since WEB-788
 */

import { parseEventInstantMs } from '@/lib/discover/timelineClock';
import { TIME_OF_DAY_HOUR_RANGES } from '@/lib/discover/timeOfDay';

/**
 * Local hour at which one night becomes the next day.
 *
 * 5 is not a new number: it is the wrap point of the `late` time-of-day block
 * (`[21, 24) ∪ [0, 5)`) that `lib/discover/timeOfDay.ts` documents and the
 * backend's `TIME_OF_DAY_RANGES` enforces. An event ending at 12:15 AM or
 * 2:00 AM is inside the same `late` block its 10:15 PM start belongs to — one
 * night — so the calendar rollover carries no information. An event still
 * running at 5:00 AM has reached the next morning, and saying so is a fact the
 * row does not otherwise carry.
 */
export const NIGHT_ROLLOVER_HOUR = 5;

/**
 * Earliest local start hour that can belong to "one night".
 *
 * The rollover exception is a claim about *both* ends of the range: a 10:15 PM
 * show finishing at 12:15 AM is one night, so the calendar flip says nothing.
 * The end hour alone cannot carry that claim — a 10:00 AM event finishing at
 * 1:00 AM the next day also ends before {@link NIGHT_ROLLOVER_HOUR}, but it is
 * a fifteen-hour run that genuinely reaches a later day, and suppressing its
 * range line would hide the only place the row says so.
 *
 * 17 is the `eve` block's `startHour` in `lib/discover/timeOfDay.ts` — an
 * evening or `late` start is the window from which a past-midnight finish
 * reads as the same night. Anything earlier is a daytime event, and its
 * rollover is information.
 */
export const NIGHT_START_HOUR = TIME_OF_DAY_HOUR_RANGES.eve.startHour;

interface LocalInstantParts {
  /** Whole days since the Unix epoch **in `timeZone`** — safe to subtract. */
  readonly dayIndex: number;
  /** 0–23 local hour. */
  readonly hour: number;
}

const partsFormatterCache = new Map<string, Intl.DateTimeFormat>();

function getPartsFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = partsFormatterCache.get(timeZone);
  if (cached) return cached;
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    // `h23` (not `hour12: false`) — some ICU builds render midnight as `24`
    // under `hour12: false`, which would push an end-of-night event a whole
    // day forward and re-introduce the very line this module suppresses.
    hourCycle: 'h23',
  };
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat('en-US', { ...options, timeZone });
  } catch {
    formatter = new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'UTC' });
  }
  partsFormatterCache.set(timeZone, formatter);
  return formatter;
}

/** Milliseconds in one whole day — the divisor turning a UTC date into a day index. */
const MS_PER_DAY = 86_400_000;

function readLocalParts(instant: string, timeZone: string): LocalInstantParts | null {
  const ms = parseEventInstantMs(instant);
  if (Number.isNaN(ms)) return null;

  const parts = getPartsFormatter(timeZone).formatToParts(new Date(ms));
  const read = (type: Intl.DateTimeFormatPartTypes): number =>
    Number.parseInt(parts.find((part) => part.type === type)?.value ?? '', 10);

  const year = read('year');
  const month = read('month');
  const day = read('day');
  const hour = read('hour');
  if (![year, month, day, hour].every((value) => Number.isFinite(value))) return null;

  // `Date.UTC` on the *local* calendar fields gives a timezone-free ordinal for
  // the local day, so the subtraction below is a plain day count and never
  // wanders across a DST transition.
  return { dayIndex: Date.UTC(year, month - 1, day) / MS_PER_DAY, hour };
}

export interface DiscoverRangeLineInput {
  /** ISO 8601 start with offset. */
  readonly startsAt: string;
  /** ISO 8601 end with offset, or absent when the wire carries no end time. */
  readonly endsAt?: string;
  /** Server-authoritative IANA zone for the event. */
  readonly timeZone: string;
}

/**
 * True when the start→end range line tells the reader something the spine and
 * day heading have not already said.
 *
 * `false` for: no end time, an unparseable instant, an end on the same local
 * day, and — the case PAR-28 reported — a night that *starts* at or after
 * {@link NIGHT_START_HOUR} and ends on the next local day before
 * {@link NIGHT_ROLLOVER_HOUR} (the ordinary past-midnight finish of one
 * night). `true` for a genuine multi-day run: a festival, a residency, a show
 * still going at breakfast, or a daytime event that runs long enough to reach
 * the small hours of the next day.
 */
export function discoverRangeLineAddsInformation(event: DiscoverRangeLineInput): boolean {
  if (event.endsAt === undefined) return false;

  const start = readLocalParts(event.startsAt, event.timeZone);
  const end = readLocalParts(event.endsAt, event.timeZone);
  if (start === null || end === null) return false;

  const dayDelta = end.dayIndex - start.dayIndex;
  // `<= 0` also absorbs a malformed row whose end precedes its start: there is
  // no honest range to draw from it.
  if (dayDelta <= 0) return false;
  // Both ends must sit inside one night for the rollover to be uninformative:
  // an evening-or-later start AND a finish before the next morning. A daytime
  // start that runs into the small hours has reached a later day, and the
  // range line is the only place the row says so.
  if (dayDelta === 1 && start.hour >= NIGHT_START_HOUR && end.hour < NIGHT_ROLLOVER_HOUR) {
    return false;
  }
  return true;
}
